import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import { secureGet, securePut } from "../../config/api";
import { formatDateTime, formatDate, formatAmount } from "../../utils/format";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../../components/Breadcrumbs";
import DetailsHeader from "../../components/DetailsHeader";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import UserContext from "../../Shared/UserContext";
import checkIcon from "../../icons/check.svg";

const STATUS_LABELS = {
  PENDING: "En attente",
  ANSWERED: "Répondu",
  VALIDATED: "Validé",
};

const STATUS_COLORS = {
  PENDING: "#FEA26F",
  ANSWERED: "#97DFFC",
  VALIDATED: "#06D6A0",
};

const PropositionDetails = () => {
  const { id: quoteId } = useParams();
  const { state: { role } } = useContext(UserContext);
  const [proposition, setProposition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answerValues, setAnswerValues] = useState({});
  const [submittingId, setSubmittingId] = useState(null);
  const [filters, setFilters] = useState({ needle: "", requestedBy: "", status: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success", manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchProposition = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/quotes/${quoteId}/propositions`, showSnackbar, { signal });
      setProposition(response.data);
      console.log(response.data)
    } catch (error) {
      if (error.name !== "AbortError") console.error("Failed to fetch propositions:", error);
    } finally {
      setLoading(false);
    }
  }, [quoteId, signal]);

  useEffect(() => {
    fetchProposition();
    return () => abortController.abort();
  }, [fetchProposition, abortController]);

  const handleAnswer = async (lineId) => {
    const rawValue = answerValues[lineId];
    const proposedPrice = parseFloat(String(rawValue ?? "").replace(",", "."));
    if (Number.isNaN(proposedPrice) || proposedPrice < 0) {
      showSnackbar("Veuillez saisir un prix valide.", "error");
      return;
    }
    setSubmittingId(lineId);
    try {
      await securePut(`/quotes/details/${lineId}/proposition/answer`, { proposedPrice }, showSnackbar);
      showSnackbar("Proposition envoyée.", "success");
      fetchProposition();
    } catch (error) {
      console.error("Failed to answer proposition:", error);
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading || !proposition) return <LoadingLogo />;

  const requesterOptions = Array.from(
    new Map(
      (proposition.lines || [])
        .filter((line) => line.requestedByName)
        .map((line) => [line.requestedByName, line.requestedByName])
    ).values()
  ).map((name) => ({ value: name, label: name }));

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "requestedBy",
      label: "Demandé par",
      type: "dropdown",
      options: requesterOptions,
    },
    {
      name: "status",
      label: "Statut",
      type: "dropdown",
      options: [
        { value: "PENDING", label: STATUS_LABELS.PENDING },
        { value: "ANSWERED", label: STATUS_LABELS.ANSWERED },
        { value: "VALIDATED", label: STATUS_LABELS.VALIDATED },
      ],
    },
  ];

  const filteredLines = (proposition.lines || []).filter((line) => {
    if (filters.needle && !(line.designation || "").toLowerCase().includes(filters.needle.toLowerCase())) return false;
    if (filters.requestedBy && line.requestedByName !== filters.requestedBy) return false;
    if (filters.status && line.priceStatus !== filters.status) return false;
    return true;
  });

  const canAnswer = role === "ROLE_STOREKEEPER" || role === "ROLE_ADMIN";

  const columns = [
    { label: "Pièce demandée", key: "designation" },
    { label: "Demandé par", key: "requestedByName" },
    { label: "Quantité", key: "quantity" },
    {
      label: "Statut",
      key: "priceStatus",
      render: (status) => (
        <span
          className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
          style={{ backgroundColor: STATUS_COLORS[status] || "#A8ADC3" }}
        >
          {STATUS_LABELS[status].toUpperCase() || status}
        </span>
      ),
    },
    {
      key: "action",
      render: (_, line) => {
        if (line.priceStatus === "PENDING") {
          if (!canAnswer) return <span className="text-gray-500">—</span>;
          return (
            <div
              className="flex items-center gap-2 border border-[#E3E3E3] rounded-[12px] pl-4 pr-1 py-1 bg-white min-w-[260px] h-[40px]"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                inputMode="decimal"
                placeholder="Répondre à cette proposition"
                value={answerValues[line.id] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value.replace(",", ".");
                  if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                    setAnswerValues((prev) => ({ ...prev, [line.id]: raw }));
                  }
                }}
                className="flex-1 min-w-0 text-[#38485C] placeholder:text-[#AFB0B2] placeholder:text-[12px] bg-transparent focus:outline-none"
              />
              <button
                type="button"
                disabled={submittingId === line.id}
                onClick={() => handleAnswer(line.id)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[#3872B0] rounded-[12px] disabled:opacity-50 shadow-[0px 4px 10px #BEBEBE50]"
              >
                <img src={checkIcon} alt="Confirmer" className="w-4 h-4" />
              </button>
            </div>
          );
        }
        return (
          <div>
            <p className="text-[#0AD69F] text-[16px] font-semibold">
              {line.proposedPrice != null ? formatAmount(Number(line.proposedPrice).toFixed(2)) : "—"}
            </p>
            {line.answeredAt && (
              <p className="text-[12px] text-[#043F88] font-semibold">
                Répondu le {formatDateTime(line.answeredAt)}
              </p>
            )}
          </div>
        );
      },
    },
  ];

  const vehicleForDetailsHeader = proposition.vehicle
    ? {
        brand: proposition.vehicle?.brand?.name || "—",
        model: proposition.vehicle?.model || "—",
        firstRegistrationDate: formatDate(proposition.vehicle?.firstRegistrationDate) || "—",
        type: proposition.vehicle?.type || "—",
        color: proposition.vehicle?.color || "—",
        plate: proposition.vehicle?.immatriculationNumber || "—",
        vin: proposition.vehicle?.vinNumber || "—",
      }
    : null;

  return (
    <div className="h-full flex flex-col">
      <Helmet>
        <title>Drivnest - Détails de la proposition</title>
      </Helmet>
      <div className="flex-shrink-0 pb-2">
        <Breadcrumbs />
        <DetailsHeader
          entityId={proposition.quoteNumber}
          client={{ code: proposition.clientCode, name: proposition.clientName }}
          complementary= {{ id: proposition?.receptionDocumentNumber, to: `/fiche-reception/details/${proposition?.receptionDocumentId}`} }
          vehicle={vehicleForDetailsHeader}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <Filter
          filters={filterOptions}
          onFilterChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
          resetFilters={() => setFilters({ needle: "", requestedBy: "", status: "" })}
          filterValues={filters}
        />
        <Table
          columns={columns}
          data={filteredLines}
          emptyLabel="Aucune proposition trouvée"
        />
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default PropositionDetails;
